import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, ShoppingCart, ClipboardList, BarChart2, Wrench,
  Database, Users, Lock, Zap, RefreshCw, Tag, AlertCircle,
  ArrowRight, Truck, Building2, HardHat, Factory, Globe,
  Briefcase, Shield, BookOpen, Bell, ChevronDown, ChevronUp,
  AlertTriangle, Clock, Hash,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// DESIGN: Refined industrial editorial — dark slate + amber gold
// Fonts: Georgia (display) + system-ui (body)
// ─────────────────────────────────────────────────────────────

const FONT_URL = 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=Outfit:wght@300;400;500;600;700&display=swap';

const css = `
  :root {
    --gold: #C9972B;
    --gold-light: #F0C96A;
    --gold-dim: #7A5A18;
    --ink: #0F1117;
    --ink-mid: #1C2030;
    --ink-soft: #262D42;
    --text-primary: #F4F0E8;
    --text-secondary: #9EA8C0;
    --text-dim: #5C657A;
    --rule: #2A3048;
    --card-bg: #161C2E;
    --card-border: #222A40;
  }

  .ps-root {
    font-family: 'Outfit', sans-serif;
    background: #0F1117;
    color: #F4F0E8;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .ps-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 50% at 20% -10%, rgba(201,151,43,0.08) 0%, transparent 60%),
      radial-gradient(ellipse 60% 60% at 80% 110%, rgba(99,102,241,0.05) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  .ps-inner {
    position: relative;
    z-index: 1;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem 6rem;
  }

  /* TYPOGRAPHY */
  .serif { font-family: 'DM Serif Display', Georgia, serif; }
  .mono  { font-family: 'DM Mono', monospace; }

  /* RULE LINE */
  .rule {
    border: none;
    border-top: 1px solid var(--rule);
    margin: 0;
  }

  /* SECTION LABEL */
  .section-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 0.75rem;
  }

  /* HERO */
  .hero {
    padding: 5rem 0 4rem;
    display: grid;
    grid-template-columns: 1.3fr 0.7fr;
    gap: 3rem;
    align-items: end;
  }
  @media (max-width: 900px) { .hero { grid-template-columns: 1fr; } }

  .hero-title {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: clamp(2.8rem, 6vw, 5.5rem);
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--text-primary);
    margin: 1rem 0 1.5rem;
  }

  .hero-title em {
    font-style: italic;
    color: var(--gold);
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: 1px solid var(--gold-dim);
    border-radius: 100px;
    padding: 0.35rem 1rem;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--gold);
    background: rgba(201,151,43,0.07);
  }

  .hero-badge-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--gold);
  }

  .hero-sub {
    font-size: 1.05rem;
    line-height: 1.8;
    color: var(--text-secondary);
    max-width: 520px;
  }

  .hero-thesis {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 20px;
    padding: 2rem;
    position: relative;
    overflow: hidden;
  }

  .hero-thesis::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--gold), transparent);
  }

  .thesis-label {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--text-dim);
    margin-bottom: 1rem;
  }

  .thesis-text {
    font-size: 0.875rem;
    line-height: 1.8;
    color: var(--text-secondary);
  }

  /* SECTION */
  .section {
    padding: 4rem 0;
    border-top: 1px solid var(--rule);
  }

  .section-header {
    margin-bottom: 2.5rem;
  }

  .section-title {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: clamp(1.8rem, 3.5vw, 2.5rem);
    line-height: 1.15;
    color: var(--text-primary);
    margin: 0.5rem 0 0.75rem;
  }

  .section-desc {
    font-size: 0.95rem;
    line-height: 1.75;
    color: var(--text-secondary);
    max-width: 480px;
  }

  /* DOMAIN GRID */
  .domain-grid {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 1.5rem;
    align-items: start;
  }
  @media (max-width: 900px) { .domain-grid { grid-template-columns: 1fr; } }

  /* STAGE BUTTON */
  .stage-btn {
    width: 100%;
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 16px;
    padding: 1.1rem 1.25rem;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.18s, background 0.18s;
    display: flex;
    align-items: center;
    gap: 1rem;
    color: var(--text-primary);
  }
  .stage-btn:hover { border-color: var(--gold-dim); }
  .stage-btn.active {
    border-color: var(--gold);
    background: rgba(201,151,43,0.08);
  }

  .stage-icon {
    width: 40px; height: 40px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .stage-num {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.15em;
    color: var(--text-dim);
    text-transform: uppercase;
  }

  .stage-name {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0.15rem 0;
    color: var(--text-primary);
  }

  .stage-sub {
    font-size: 0.78rem;
    color: var(--text-dim);
  }

  .stage-connector {
    width: 1px;
    height: 14px;
    background: var(--rule);
    margin-left: 35px;
  }

  /* DETAIL PANEL */
  .detail-panel {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 20px;
    padding: 2.5rem;
    position: relative;
    overflow: hidden;
    min-height: 400px;
  }

  .detail-panel::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
  }

  .detail-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .detail-icon {
    width: 52px; height: 52px;
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
  }

  .detail-domain-label {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--text-dim);
  }

  .detail-title {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.6rem;
    color: var(--text-primary);
    margin: 0.2rem 0 0;
  }

  .detail-summary {
    font-size: 0.875rem;
    line-height: 1.85;
    color: var(--text-secondary);
    margin-bottom: 1.75rem;
  }

  .detail-caveat {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    background: rgba(201,151,43,0.07);
    border: 1px solid rgba(201,151,43,0.2);
    border-radius: 10px;
    padding: 0.75rem 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
    line-height: 1.6;
    color: var(--gold-light);
  }

  .bullets-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
  }
  @media (max-width: 640px) { .bullets-grid { grid-template-columns: 1fr; } }

  .bullet-item {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    border: 1px solid var(--card-border);
    border-radius: 10px;
    padding: 0.75rem 0.875rem;
    background: rgba(255,255,255,0.02);
    font-size: 0.8rem;
    line-height: 1.55;
    color: var(--text-secondary);
  }

  .bullet-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 5px;
  }

  /* PILLARS */
  .pillars-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }
  @media (max-width: 900px) { .pillars-grid { grid-template-columns: repeat(2,1fr); } }
  @media (max-width: 600px) { .pillars-grid { grid-template-columns: 1fr; } }

  .pillar-card {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 16px;
    padding: 1.5rem;
    transition: border-color 0.2s;
  }
  .pillar-card:hover { border-color: var(--gold-dim); }

  .pillar-icon {
    width: 40px; height: 40px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 1rem;
  }

  .pillar-title {
    font-weight: 700;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
  }

  .pillar-body {
    font-size: 0.82rem;
    line-height: 1.7;
    color: var(--text-secondary);
  }

  .pillar-tension {
    display: inline-block;
    margin-top: 0.5rem;
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    background: rgba(201,151,43,0.1);
    color: var(--gold);
  }

  /* REMEMBER SECTION */
  .remember-card {
    background: var(--card-bg);
    border: 1px solid var(--gold-dim);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
  }

  .remember-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid var(--rule);
    cursor: pointer;
    user-select: none;
  }

  .remember-bell {
    width: 42px; height: 42px;
    border-radius: 12px;
    background: rgba(201,151,43,0.15);
    border: 1px solid var(--gold-dim);
    display: flex; align-items: center; justify-content: center;
    color: var(--gold);
    flex-shrink: 0;
  }

  .remember-title {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.3rem;
    color: var(--text-primary);
    flex: 1;
  }

  .remember-sub {
    font-size: 0.8rem;
    color: var(--text-dim);
    margin-top: 0.15rem;
  }

  .remember-body {
    padding: 1.75rem 2rem;
  }

  .remember-intro {
    font-size: 0.875rem;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: 1.5rem;
  }

  .remember-items {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  @media (max-width: 700px) { .remember-items { grid-template-columns: 1fr; } }

  .remember-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    background: rgba(201,151,43,0.04);
    border: 1px solid rgba(201,151,43,0.12);
    border-radius: 12px;
    padding: 0.875rem 1rem;
  }

  .remember-icon-wrap {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: rgba(201,151,43,0.1);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    color: var(--gold);
  }

  .remember-item-title {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }

  .remember-item-body {
    font-size: 0.78rem;
    line-height: 1.6;
    color: var(--text-secondary);
  }

  /* USER TYPES */
  .user-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .user-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    border-radius: 100px;
    padding: 0.35rem 0.875rem;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    border: 1px solid;
  }

  /* CONNECTED VISION */
  .vision-grid {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 1.5rem;
    align-items: stretch;
  }
  @media (max-width: 900px) { .vision-grid { grid-template-columns: 1fr; } }

  .vision-card {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 20px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
  }

  .vision-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--gold), var(--gold-dim), transparent);
  }

  .vision-quote {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 1.4rem;
    line-height: 1.4;
    color: var(--text-primary);
    margin: 1.5rem 0;
  }

  .vision-quote em { color: var(--gold); font-style: italic; }

  .vision-body {
    font-size: 0.85rem;
    line-height: 1.8;
    color: var(--text-secondary);
  }

  .lifecycle-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--rule);
  }

  .lifecycle-tag {
    font-size: 11px;
    font-weight: 500;
    border-radius: 6px;
    padding: 0.25rem 0.6rem;
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--rule);
    color: var(--text-secondary);
  }

  .systems-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
  @media (max-width: 600px) { .systems-grid { grid-template-columns: repeat(2,1fr); } }

  .sys-card {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 14px;
    padding: 1.25rem;
    transition: border-color 0.2s;
  }
  .sys-card:hover { border-color: var(--gold-dim); }

  .sys-icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 0.75rem;
    border: 1px solid var(--rule);
  }

  .sys-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.35rem;
  }

  .sys-blurb {
    font-size: 0.78rem;
    line-height: 1.6;
    color: var(--text-dim);
  }

  /* FOOTER */
  .ps-footer {
    margin-top: 4rem;
    padding-top: 2rem;
    border-top: 1px solid var(--rule);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .footer-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--text-dim);
  }

  .version-badge {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    padding: 0.25rem 0.6rem;
    border: 1px solid var(--gold-dim);
    border-radius: 6px;
    color: var(--gold);
    letter-spacing: 0.1em;
  }
`;

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────

const stages = [
  {
    id: "design",
    num: "01",
    label: "Design Management",
    sub: "Every product starts with a spec.",
    Icon: FileText,
    iconBg: "background: rgba(14,165,233,0.15); color: #38bdf8;",
    accentColor: "#38bdf8",
    gradientBar: "linear-gradient(90deg, #0ea5e9, transparent)",
    summary:
      "Engineering owns the technical content. Procurement tracks it, logs it, and ensures it translates to the project level. Design Management is the single source of truth for every product spec — SKUs, logistics details, storage requirements, nameplate photos, and the living record of every design change after approval. When a design changes, the downstream ripple — across commissioning checklists, spares, field mods — is visible before it becomes a problem.",
    bullets: [
      "Program documentation: upload, date-stamp, approval status per revision",
      "Design change tracking — field mods, safety redesigns, Ops redesigns, Delta Cube variants",
      "Logistics library: weight, dims, pallet count, truck count, packaging photos",
      "Storage requirements defined at the program level",
      "Quality system link: open SCARs and alerts surface on every vendor-product lookup",
      "Region-specific and AHJ requirements repository",
      "Tenant-specific and program-specific design requirement files",
    ],
  },
  {
    id: "buyout",
    num: "02",
    label: "Buyout",
    sub: "Turning plans into purchase orders.",
    Icon: ShoppingCart,
    iconBg: "background: rgba(16,185,129,0.15); color: #34d399;",
    accentColor: "#34d399",
    gradientBar: "linear-gradient(90deg, #10b981, transparent)",
    summary:
      "The OFE Plan from business leadership — megawatts, blocks, program, priority — becomes the input that drives everything downstream. Oracle POs capture the commercial reality. The gap today is that Oracle lines are entered manually, often with quantities greater than one, making unit-price analysis across all cost types nearly impossible. The future system explodes every multi-unit line automatically and ensures every line — freight, services, storage, change orders — carries a project allocation. Every single line. No exceptions.",
    caveat: "Today this is almost entirely manual — and the QTY > 1 problem is a real automation wall. Fixing this is foundational, not optional.",
    bullets: [
      "OFE Plan inputs: MW, blocks, design program, priority order drive system quantities",
      "Auto-populate Oracle PO data into Production Planners — no manual re-entry",
      "Smart line explosion: QTY > 1 on a PO line splits to one row per unit",
      "Unit prices enforced across all cost types: product, freight, services, misc",
      "Tax-applicability flag per project — never forgotten on a taxable PO",
      "Vendor Profiles: contacts, org chart, MSA tracking, milestone payments, quality assessments",
      "Auto-file every PO copy to a designated folder on creation",
    ],
  },
  {
    id: "production",
    num: "03",
    label: "Production Planning",
    sub: "One row. One asset. Full visibility.",
    Icon: ClipboardList,
    iconBg: "background: rgba(139,92,246,0.15); color: #a78bfa;",
    accentColor: "#a78bfa",
    gradientBar: "linear-gradient(90deg, #8b5cf6, transparent)",
    summary:
      "This is where each product becomes its own asset. Every unit — quantity one per row — is tracked from manufacturing through first delivery. Vendors can access their own planner to update production status, ship dates, and serial numbers. Internal teams see on-time performance, storage addresses, and contractual dates. When a project gets reallocated, the system tracks the history. When a vendor updates a field, access controls ensure they only touch what they're permitted to touch.",
    bullets: [
      "QTY 1 per row — every unit is an individually tracked asset from day one",
      "Vendor-accessible: controlled by vendor, by planner, and by data column",
      "Production status, MFG ship date (plan vs actual), on-time performance",
      "Vendor storage tracking: address, out-of-storage date, first delivery destination",
      "Serial number assignment and capture at the unit level",
      "Contractual date vs requested delivery — gap always visible",
      "Access control by internal ADC team — not every team sees every planner",
    ],
  },
  {
    id: "project",
    num: "04",
    label: "Project Management",
    sub: "Schedule, dollars, and documents — unified.",
    Icon: BarChart2,
    iconBg: "background: rgba(245,158,11,0.15); color: #fbbf24;",
    accentColor: "#fbbf24",
    gradientBar: "linear-gradient(90deg, #f59e0b, transparent)",
    summary:
      "Project Management pulls from Production Planners and Oracle in real time. The Project Schedule auto-populates from vendor allocations. Project Financials roll up every PO line by project and PFR code, with a manual exposure layer for PM-entered risk costs. Submittals tracked internally with SharePoint links — not locked inside a GC-controlled Procore. Contacts drawn from the central Vendor Profile database. AI determines which costs need a Journal Entry to move between projects — and flags when the move is complete.",
    caveat: "Adding automation to submittal tracking is acknowledged as 'future-future' — the priority is ownership of the data trail first.",
    bullets: [
      "Project Schedule auto-filled from Production Planner allocations",
      "Reallocation history tracked — FinCode detail replaced by system audit trail",
      "Project Financials: PO + exposure subtotals, PFR code rollup, by-project view",
      "AI-assisted Journal Entry identification: which line, which project, which cost",
      "Submittals tracked internally — ADC-owned, not GC-gated",
      "Storage Locations database: type, address, POC, hours, insurance, warehouse ID",
      "Project Contacts: vendors from profiles, GC, EC, MC, EOR, CxA — all linked",
    ],
  },
  {
    id: "cx",
    num: "05",
    label: "CX & Field Scope",
    sub: "The last mile: from vendor to operations.",
    Icon: Wrench,
    iconBg: "background: rgba(244,63,94,0.15); color: #fb7185;",
    accentColor: "#fb7185",
    gradientBar: "linear-gradient(90deg, #f43f5e, transparent)",
    summary:
      "Procurement hands off to Construction — but the equipment's story doesn't end there. The future system tracks from GC storage through installation, commissioning stages, and turnover to Ops. CxAlloy becomes a data feed, not a silo. The Cx Look Ahead — a monthly report to all vendors showing project phase timelines — backfeeds into Production Planners so vendors always know where their equipment is heading. Serial number is the link across every handoff. The OFE Cx Playbook is a candidate for AI-assisted schedule sequencing.",
    caveat: "Procurement doesn't manage much additional data beyond what exists elsewhere in this plan. The biggest gap is anything that happens between Procurement delivery to GC storage and when CxAlloy picks it up.",
    bullets: [
      "Post-delivery status: GC storage → installation → Cx stages → Ops turnover",
      "CxAlloy integration: installation and beyond status feeds back into the system",
      "Cx Look Ahead: monthly vendor-facing schedule by project, phase, and L2–RFS milestones",
      "Look Ahead data backfeeds Production Planners — vendors always see field timing",
      "3PL sync: product movement after delivery automated, no manual updates",
      "Serial number as the universal link from manufacture to operations",
      "OFE Cx Playbook: potential for AI-assisted sequencing and schedule automation",
    ],
  },
];

const systemPillars = [
  {
    Icon: Lock,
    iconStyle: "background: rgba(244,63,94,0.12); color: #fb7185;",
    title: "Access Control — Paramount",
    body: "User type governs everything. Vendors see only their planner, only permitted columns. Internal teams are scoped by function. External contractors get what they need and nothing more. This is the non-negotiable foundation.",
    tension: "The whole system breaks without this",
  },
  {
    Icon: Zap,
    iconStyle: "background: rgba(14,165,233,0.12); color: #38bdf8;",
    title: "Oracle Auto-Population",
    body: "Today, Procurement re-enters Oracle PO data by hand into every Production Planner. The future system reads Oracle directly — and when a line has QTY > 1, it explodes it into individual asset rows.",
    tension: "Currently manual — every time",
  },
  {
    Icon: Tag,
    iconStyle: "background: rgba(139,92,246,0.12); color: #a78bfa;",
    title: "Unit Prices Across All Costs",
    body: "Product enters Oracle at unit price. Freight, services, and storage often enter lump-sum as QTY 1. The system enforces unit-price capture universally — you cannot manage cost exposure you cannot see at the line level.",
    tension: "Core automation blocker today",
  },
  {
    Icon: RefreshCw,
    iconStyle: "background: rgba(16,185,129,0.12); color: #34d399;",
    title: "Live Reallocation Tracking",
    body: "When equipment moves from one project to another, every linked field — PO line, cost, schedule row — moves with it. History of every reallocation is preserved automatically. No more manual FinCode spreadsheets.",
    tension: "All manual today",
  },
  {
    Icon: Database,
    iconStyle: "background: rgba(245,158,11,0.12); color: #fbbf24;",
    title: "Serial Number as the Spine",
    body: "From the Production Planner to CxAlloy to the 3PL system, serial number is the universal key. Every handoff, every status change, every document traces back to one unique asset identifier.",
    tension: "The linking mechanism across everything",
  },
  {
    Icon: AlertCircle,
    iconStyle: "background: rgba(201,151,43,0.12); color: #C9972B;",
    title: "AI-Assisted Financials",
    body: "The system uses AI to identify which PO line costs need a Journal Entry to move between projects — and tracks when the entry is complete. Tax applicability flags prevent forgotten charges.",
    tension: "Highest leverage AI use case",
  },
];

const rememberItems = [
  {
    Icon: Hash,
    title: "Tax applicability per project",
    body: "No current Oracle flag. Without a system prompt, taxable POs get missed. Build the reminder in — don't rely on memory.",
  },
  {
    Icon: Truck,
    title: "All PO lines need allocation",
    body: "Every freight, storage, and change order line needs a linked Project Allocation or it can't move when product gets reallocated.",
  },
  {
    Icon: Clock,
    title: "GC handoff gap",
    body: "Status tracking goes dark between Procurement delivery and CxAlloy pickup. Someone has to own that window — or the system has to flag it.",
  },
  {
    Icon: AlertTriangle,
    title: "Design change downstream effects",
    body: "A redesign after approval hits Cx checklists, spares, and field mods. The system needs to surface those ripples automatically.",
  },
  {
    Icon: Building2,
    title: "Submittal access after GC turnover",
    body: "GC-controlled Procore goes dark after project close. ADC-owned copies in SharePoint are the only long-term record. Never skip the internal filing.",
  },
  {
    Icon: Users,
    title: "Vendor-specific column access",
    body: "Vendor A must never see Vendor B's data — even in the same planner. Access must be enforced at the column and vendor level, not just the page level.",
  },
];

const userTypes = [
  { label: "Procurement",          style: "color:#a78bfa; background:rgba(139,92,246,0.08); border-color:rgba(139,92,246,0.25);", internal: true },
  { label: "Engineering",          style: "color:#38bdf8; background:rgba(14,165,233,0.08); border-color:rgba(14,165,233,0.25);",  internal: true },
  { label: "Project Mgmt",         style: "color:#fbbf24; background:rgba(245,158,11,0.08);  border-color:rgba(245,158,11,0.25);",  internal: true },
  { label: "Vendors",              style: "color:#34d399; background:rgba(16,185,129,0.08);  border-color:rgba(16,185,129,0.25);",  internal: false },
  { label: "General Contractor",   style: "color:#fb923c; background:rgba(249,115,22,0.08);  border-color:rgba(249,115,22,0.25);",  internal: false },
  { label: "Integrators",          style: "color:#2dd4bf; background:rgba(20,184,166,0.08);  border-color:rgba(20,184,166,0.25);",  internal: false },
  { label: "3PL Warehouses",       style: "color:#60a5fa; background:rgba(59,130,246,0.08);  border-color:rgba(59,130,246,0.25);",  internal: false },
  { label: "CxA",                  style: "color:#f472b6; background:rgba(236,72,153,0.08);  border-color:rgba(236,72,153,0.25);",  internal: false },
  { label: "Electrical Contractor",style: "color:#818cf8; background:rgba(99,102,241,0.08);  border-color:rgba(99,102,241,0.25);",  internal: false },
  { label: "EOR",                  style: "color:#f87171; background:rgba(239,68,68,0.08);   border-color:rgba(239,68,68,0.25);",   internal: false },
  { label: "Operations",           style: "color:#94a3b8; background:rgba(148,163,184,0.08); border-color:rgba(148,163,184,0.25);", internal: false },
];

const connectedSystems = [
  { Icon: Database,  style: "color:#fb923c; background:rgba(249,115,22,0.1); border-color:rgba(249,115,22,0.2);",  label: "Oracle ERP",      blurb: "Auto-reads every PO line, explodes quantities, allocates every cost — no manual re-entry." },
  { Icon: Factory,   style: "color:#a78bfa; background:rgba(139,92,246,0.1); border-color:rgba(139,92,246,0.2);",  label: "CxAlloy",         blurb: "Commissioning and field status feeds back by serial number — from purchase order to operational asset." },
  { Icon: Truck,     style: "color:#34d399; background:rgba(16,185,129,0.1); border-color:rgba(16,185,129,0.2);",  label: "3PL Systems",     blurb: "Inventory movement synced automatically. The moment product leaves the warehouse, ADC knows — no phone call." },
  { Icon: Shield,    style: "color:#60a5fa; background:rgba(59,130,246,0.1); border-color:rgba(59,130,246,0.2);",  label: "ServiceNow",      blurb: "Eventually: quality events, SCARs, and service requests link to Vendor Profiles and Design Management." },
  { Icon: Globe,     style: "color:#38bdf8; background:rgba(14,165,233,0.1); border-color:rgba(14,165,233,0.2);",  label: "SharePoint",      blurb: "Submittals filed internally — ADC-owned, not locked in GC-controlled Procore after project close." },
  { Icon: BookOpen,  style: "color:#f472b6; background:rgba(236,72,153,0.1); border-color:rgba(236,72,153,0.2);",  label: "Procore / Helix", blurb: "GC-managed platforms tracked in parallel. ADC never loses visibility when a GC controls the primary tool." },
];

const lifecycleTags = [
  "Program Design","Vendor Qualification","PO Issuance",
  "Manufacturing","Logistics","First Delivery",
  "GC Storage","Installation","Commissioning","Ops Turnover",
];

// ─────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────

function StageButton({ stage, active, onClick, showConnector }) {
  const isActive = active.id === stage.id;
  const { Icon } = stage;
  return (
    <>
      <button
        onClick={() => onClick(stage)}
        className={`stage-btn${isActive ? " active" : ""}`}
      >
        <div className="stage-icon" style={{ ...parseStyle(stage.iconBg) }}>
          <Icon size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="stage-num">{stage.num}</div>
          <div className="stage-name">{stage.label}</div>
          <div className="stage-sub">{stage.sub}</div>
        </div>
        <ArrowRight size={14} style={{ color: isActive ? "var(--gold)" : "var(--text-dim)", flexShrink: 0 }} />
      </button>
      {showConnector && <div className="stage-connector" />}
    </>
  );
}

function DetailPanel({ stage }) {
  const { Icon } = stage;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stage.id}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.22 }}
      >
        <div className="detail-panel" style={{ "--accent": stage.accentColor }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: stage.gradientBar }} />
          <div className="detail-header">
            <div className="detail-icon" style={{ ...parseStyle(stage.iconBg) }}>
              <Icon size={22} />
            </div>
            <div>
              <div className="detail-domain-label">Domain {stage.num}</div>
              <div className="detail-title">{stage.label}</div>
            </div>
          </div>
          <p className="detail-summary">{stage.summary}</p>
          {stage.caveat && (
            <div className="detail-caveat">
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2, color: "var(--gold)" }} />
              <span>{stage.caveat}</span>
            </div>
          )}
          <div className="bullets-grid">
            {stage.bullets.map((b) => (
              <div key={b} className="bullet-item">
                <div className="bullet-dot" style={{ background: stage.accentColor }} />
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function parseStyle(str) {
  const result = {};
  str.split(";").forEach(s => {
    const [k, v] = s.split(":").map(x => x.trim());
    if (k && v) {
      const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      result[camel] = v;
    }
  });
  return result;
}

// ─────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────

export default function ProcurementStory() {
  const [activeStage, setActiveStage] = useState(stages[0]);
  const [rememberOpen, setRememberOpen] = useState(false);

  return (
    <div className="ps-root">
      <link rel="stylesheet" href={FONT_URL} />
      <style>{css}</style>
      <div className="ps-inner">

        {/* HERO */}
        <motion.section
          className="hero"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <div>
            <div className="hero-badge">
              <div className="hero-badge-dot" />
              ADC Procurement System — Vision
            </div>
            <h1 className="hero-title serif">
              From design spec<br />
              to <em>operational</em><br />
              asset.
            </h1>
            <p className="hero-sub">
              Today, almost every data point that moves between Design, Buyout,
              Production, and the Field is a manual entry. The future system
              connects all five domains — so data flows once, and every team
              always sees the truth.
            </p>
          </div>
          <div className="hero-thesis">
            <div className="thesis-label">Core thesis</div>
            <p className="thesis-text">
              User access control is paramount. Vendors see only their data.
              Teams see only what they need. Every unit of equipment is tracked
              as its own asset — from manufacture to operations — with serial
              number as the universal key.
            </p>
            <p className="thesis-text" style={{ marginTop: "1rem" }}>
              The same connected system that auto-populates Production Planners
              from Oracle also feeds the Project Schedule, rolls up financials
              by PFR code, and links quality events to every vendor-product lookup.
            </p>
          </div>
        </motion.section>

        {/* 01 — FIVE DOMAINS */}
        <section className="section">
          <div className="section-header">
            <div className="section-label">01 / The Five Domains</div>
            <h2 className="section-title serif">Start with design.<br />End with operations.</h2>
            <p className="section-desc">
              Five connected domains, one system. Each domain feeds the next.
              Data entered once propagates forward — no re-keying, no version drift, no lost handoffs.
            </p>
          </div>
          <div className="domain-grid">
            <div>
              {stages.map((stage, idx) => (
                <StageButton
                  key={stage.id}
                  stage={stage}
                  active={activeStage}
                  onClick={setActiveStage}
                  showConnector={idx < stages.length - 1}
                />
              ))}
            </div>
            <DetailPanel stage={activeStage} />
          </div>
        </section>

        {/* 02 — SYSTEM PILLARS */}
        <section className="section">
          <div className="section-header">
            <div className="section-label">02 / System Design Principles</div>
            <h2 className="section-title serif">Six rules the system<br />never breaks.</h2>
            <p className="section-desc">
              These aren't features — they're the structural commitments that make every other
              capability trustworthy. Get these wrong and the whole system collapses back into spreadsheets.
            </p>
          </div>
          <div className="pillars-grid">
            {systemPillars.map((p, i) => {
              const { Icon } = p;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.07 }}
                  viewport={{ once: true }}
                  className="pillar-card"
                >
                  <div className="pillar-icon" style={{ ...parseStyle(p.iconStyle) }}>
                    <Icon size={18} />
                  </div>
                  <div className="pillar-title">{p.title}</div>
                  <p className="pillar-body">{p.body}</p>
                  {p.tension && <span className="pillar-tension">{p.tension}</span>}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* 03 — DID YOU REMEMBER */}
        <section className="section">
          <div className="section-header">
            <div className="section-label">03 / Built-In Memory</div>
            <h2 className="section-title serif">"Did you remember?"</h2>
            <p className="section-desc">
              Some things don't happen consistently enough to be fully systematized — but they
              happen often enough to hurt when missed. The system surfaces these as pop-up reminders
              at the right moment, not as items buried in a runbook.
            </p>
          </div>
          <div className="remember-card">
            <div
              className="remember-header"
              onClick={() => setRememberOpen(!rememberOpen)}
            >
              <div className="remember-bell">
                <Bell size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="remember-title serif">Pop-Up Reminder System</div>
                <div className="remember-sub">
                  Institutional knowledge encoded into the platform — not stored in someone's head
                </div>
              </div>
              {rememberOpen
                ? <ChevronUp size={18} style={{ color: "var(--gold)", flexShrink: 0 }} />
                : <ChevronDown size={18} style={{ color: "var(--text-dim)", flexShrink: 0 }} />
              }
            </div>
            <AnimatePresence>
              {rememberOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="remember-body">
                    <p className="remember-intro">
                      Much of this can be built into system logic — but there are situations that
                      don't happen consistently enough to fully automate, yet do happen consistently
                      enough to forget. The system should surface these at the right workflow moment,
                      not rely on the team to remember them under pressure.
                    </p>
                    <div className="remember-items">
                      {rememberItems.map((item) => {
                        const { Icon } = item;
                        return (
                          <div key={item.title} className="remember-item">
                            <div className="remember-icon-wrap">
                              <Icon size={14} />
                            </div>
                            <div>
                              <div className="remember-item-title">{item.title}</div>
                              <div className="remember-item-body">{item.body}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* 04 — USER TYPES */}
        <section className="section">
          <div className="section-header">
            <div className="section-label">04 / Who Uses It</div>
            <h2 className="section-title serif">One system,<br />eleven stakeholders.</h2>
            <p className="section-desc">
              Access control is the foundation — not an afterthought. Every user type has a defined
              scope: what they can see, what they can edit, and which projects they can touch.
            </p>
          </div>
          <div className="user-chips">
            {userTypes.map((u) => (
              <span key={u.label} className="user-chip" style={{ ...parseStyle(u.style) }}>
                {u.internal && (
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
                )}
                {u.label}
                {u.internal && (
                  <span style={{ fontFamily: "DM Mono, monospace", fontSize: 8, letterSpacing: "0.1em", opacity: 0.6 }}>INTERNAL</span>
                )}
              </span>
            ))}
          </div>
          <div style={{ marginTop: "1rem", display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--text-dim)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--gold)", display: "inline-block" }} />
              Internal = ADC team with full or role-scoped access
            </span>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
              All others: external access with column-level and project-level controls
            </span>
          </div>
        </section>

        {/* 05 — CONNECTED VISION */}
        <section className="section">
          <div className="section-header">
            <div className="section-label">05 / The Connected System</div>
            <h2 className="section-title serif">One spine.<br />Every system reports to it.</h2>
            <p className="section-desc">
              The future procurement platform isn't a standalone tool — it's the connective tissue
              between Oracle, CxAlloy, ServiceNow, SharePoint, and every 3PL and GC system in the field.
            </p>
          </div>
          <div className="vision-grid">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="vision-card" style={{ height: "100%" }}>
                <div>
                  <div style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: "rgba(201,151,43,0.12)", border: "1px solid var(--gold-dim)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--gold)"
                  }}>
                    <HardHat size={22} />
                  </div>
                  <div className="vision-quote serif">
                    Data enters <em>once.</em><br />
                    Every team sees the truth.
                  </div>
                  <p className="vision-body">
                    Today: a Procurement team member touches the same data point in Oracle,
                    a Production Planner, a Project Schedule, and a financial report —
                    four times, by hand, every time something changes.
                  </p>
                  <p className="vision-body" style={{ marginTop: "1rem" }}>
                    The future system: Oracle feeds the Planner. The Planner feeds the Schedule.
                    The Schedule feeds the financial view. Serial numbers connect every system
                    from manufacture to Ops. And AI handles the rest.
                  </p>
                </div>
                <div className="lifecycle-tags">
                  {lifecycleTags.map((tag) => (
                    <span key={tag} className="lifecycle-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </motion.div>

            <div className="systems-grid">
              {connectedSystems.map((item, i) => {
                const { Icon } = item;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.07 }}
                    viewport={{ once: true }}
                    className="sys-card"
                  >
                    <div className="sys-icon" style={{ ...parseStyle(item.style) }}>
                      <Icon size={17} />
                    </div>
                    <div className="sys-name">{item.label}</div>
                    <div className="sys-blurb">{item.blurb}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <div className="ps-footer">
          <div className="footer-label">ADC Procurement System — Internal Vision Document</div>
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            <span className="footer-label">Built from the original system diagram</span>
            <span className="version-badge">v2.0</span>
          </div>
        </div>

      </div>
    </div>
  );
}
